import { CompanySettings } from '__generated__/GQL';
import { InvoiceFormSchema } from 'features/Work/form/invoiceForm.schema';
import {
  formatSharedBaseValues,
  formatSharedSettingsValues,
} from 'features/Work/utils';
import { datetimeToLocalDateUtc, formatDate, LLLL_yyyy } from 'helpers/date';
import { capitalize } from 'helpers/string';

import {
  createEmptyTask,
  DEFAULT_LATE_MAX_DAYS,
  MINIMUM_LATE_FEE_FALLBACK,
} from '../../../../constants';
import { FormCompany } from '../../../../types';

export const defaultCompany: FormCompany = {
  city: '',
  country: '',
  isMicro: false,
  legalForm: '',
  legalName: '',
  legalRegistrationNumber: '',
  legalShareCapital: 0,
  legalVATNumber: '',
  rcsNumber: null,
  rmNumber: null,
  street: '',
  tradeName: '',
  zip: '',
};

const defaultSettings: InvoiceFormSchema['settings'] = {
  displayTradeName: true,
  invoiceSettingsId: '',
  issuerEmail: '',
  legalMentions: [],
};

export const DEFAULT_MAX_CREDIT_CARD_PAYMENT = 500;

export const defaultInvoice: InvoiceFormSchema = {
  allowCreditCardPayment: true,
  allowIbanPayment: true,
  clientId: '',
  companyProfileId: '',
  currency: 'EUR',
  fiscalId: '',
  lastEditedAt: new Date().valueOf(),
  lateFeePercentage: MINIMUM_LATE_FEE_FALLBACK,
  lateMaxDays: DEFAULT_LATE_MAX_DAYS,
  publicPaymentId: '',
  settings: defaultSettings,
  startAt: datetimeToLocalDateUtc(new Date()).valueOf(),
  subtitle: '',
  tasks: [createEmptyTask()],
  title: capitalize(formatDate(new Date(), LLLL_yyyy)),
};

export const applyPresetsToInvoice = (
  invoice: InvoiceFormSchema,
  settings?: CompanySettings,
): InvoiceFormSchema => ({
  ...invoice,
  ...formatSharedBaseValues(invoice, settings),
  settings: {
    ...invoice.settings,
    ...formatSharedSettingsValues(invoice.settings, settings),
  },
});

interface ApplyCacheToInvoiceArgs {
  baseInvoice: InvoiceFormSchema;
  cachedInvoice: InvoiceFormSchema;
}

/**
 * `cachedInvoice` is retrieved from the locale storage and could include undesired values.
 * This function will apply the cached values to the base invoice.
 * Done property by property because spreading could add undesired hidden properties.
 */
export const applyCacheToInvoice = ({
  baseInvoice,
  cachedInvoice,
}: ApplyCacheToInvoiceArgs): InvoiceFormSchema => {
  const baseTask = createEmptyTask();

  return {
    allowCreditCardPayment:
      cachedInvoice.allowCreditCardPayment ??
      baseInvoice.allowCreditCardPayment,
    allowIbanPayment:
      cachedInvoice.allowIbanPayment ?? baseInvoice.allowIbanPayment,
    clientId: cachedInvoice.clientId ?? baseInvoice.clientId,
    companyProfileId: baseInvoice.companyProfileId,
    currency: baseInvoice.currency,
    fiscalId: cachedInvoice.fiscalId ?? baseInvoice.fiscalId,
    lastEditedAt: cachedInvoice.lastEditedAt,
    lateFeePercentage:
      cachedInvoice.lateFeePercentage ?? baseInvoice.lateFeePercentage,
    lateMaxDays: cachedInvoice.lateMaxDays ?? baseInvoice.lateMaxDays,
    publicPaymentId: baseInvoice.publicPaymentId,
    settings: {
      displayTradeName:
        cachedInvoice.settings.displayTradeName ??
        baseInvoice.settings.displayTradeName,
      invoiceSettingsId: baseInvoice.settings.invoiceSettingsId,
      issuerEmail:
        cachedInvoice.settings.issuerEmail ?? baseInvoice.settings.issuerEmail,
      legalMentions:
        cachedInvoice.settings.legalMentions ??
        baseInvoice.settings.legalMentions,
    },
    startAt: baseInvoice.startAt,
    subtitle: cachedInvoice.subtitle ?? baseInvoice.subtitle,
    tasks:
      cachedInvoice.tasks.map((cachedTask) => ({
        invoiceCatalogItemId:
          cachedTask.invoiceCatalogItemId ?? baseTask.invoiceCatalogItemId,
        invoiceTaskId: cachedTask.invoiceTaskId,
        subtitle: cachedTask.subtitle ?? baseTask.subtitle,
        title: cachedTask.title ?? baseTask.title,
        type: cachedTask.type ?? baseTask.type,
        unitPrice: cachedTask.unitPrice ?? baseTask.unitPrice,
        units: cachedTask.units ?? baseTask.units,
        vatPercentage: cachedTask.vatPercentage ?? baseTask.vatPercentage,
      })) ?? baseInvoice.tasks,
    title: cachedInvoice.title ?? baseInvoice.title,
  };
};

const ONE_HOUR = 60 * 60 * 1000;
export const TTL = ONE_HOUR;
